import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { authAPI, userAPI } from '../services/api';
import { AlertCircle, CheckCircle, CreditCard } from 'lucide-react';

const PaymentSettings = ({ embedded = false }) => {
  const { updateUser } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [status, setStatus] = useState(null); // { connected, onboardingComplete, payoutsEnabled }
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [balance, setBalance] = useState(null); // { instantAvailable, available, pending }
  const [cashingOut, setCashingOut] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const res = await userAPI.getStripeConnectStatus();
      setStatus(res.data.data);
      if (res.data.data?.onboardingComplete) {
        try {
          const bal = await userAPI.getInstantBalance();
          setBalance(bal.data.data);
        } catch (e) { /* noop */ }
      }
    } catch (err) {
      console.error('Failed to load payout status:', err);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const handleInstantPayout = async () => {
    setCashingOut(true);
    setError('');
    setSuccess('');
    try {
      const res = await userAPI.requestInstantPayout();
      setSuccess(res.data?.message || 'Instant payout sent to your debit card.');
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Instant payout failed');
    } finally {
      setCashingOut(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Returning from Stripe onboarding (return_url adds ?success=true)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSuccess('Stripe account connected! Verifying your payout status…');
      (async () => {
        await loadStatus();
        try {
          const me = await authAPI.getMe();
          updateUser(me.data.data);
        } catch (e) { /* noop */ }
      })();
      window.history.replaceState({}, '', '/payment-settings');
    }
  }, [loadStatus, updateUser]);

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    setSuccess('');
    try {
      // If onboarding is finished, open their live Stripe Express dashboard
      // (where they see their balance/payouts). Otherwise start/continue onboarding.
      const res = status?.onboardingComplete
        ? await userAPI.stripeConnectLoginLink()
        : await userAPI.stripeConnectOnboarding();
      const url = res.data?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setError('Could not open Stripe. Please try again.');
        setConnecting(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to open Stripe');
      setConnecting(false);
    }
  };

  const connected = status?.connected;
  const complete = status?.onboardingComplete;

  return (
    <div style={embedded ? {} : { maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>
      <h1 style={{ marginBottom: '10px', fontSize: embedded ? '20px' : undefined }}>
        {embedded ? 'Payouts' : 'Payout Settings'}
      </h1>
      <p style={{ color: 'var(--textSecondary)', marginBottom: '32px' }}>
        Connect your bank account with Stripe to receive payouts from your sales.
      </p>

      {error && (
        <div style={{ padding: '16px', background: '#fee', border: '1px solid var(--error)', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AlertCircle size={20} color="var(--error)" />
          <span style={{ color: 'var(--error)' }}>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ padding: '16px', background: '#efe', border: '1px solid var(--success)', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CheckCircle size={20} color="var(--success)" />
          <span style={{ color: 'var(--success)' }}>{success}</span>
        </div>
      )}

      {/* Stripe Connect Card */}
      <div style={{ background: 'var(--cardBackground)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '16px', marginBottom: '20px' }}>
          <div style={{ width: '48px', height: '48px', background: complete ? 'var(--success)' : '#635bff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {complete ? <CheckCircle size={24} color="white" /> : <CreditCard size={24} color="white" />}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ marginTop: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              Stripe Payouts
              {!loadingStatus && (
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'white',
                  background: complete ? 'var(--success)' : connected ? '#f5a623' : 'var(--textSecondary)',
                }}>
                  {complete ? 'Active' : connected ? 'Action needed' : 'Not connected'}
                </span>
              )}
            </h2>
            <p style={{ color: 'var(--textSecondary)', margin: 0 }}>
              {complete
                ? 'Your account is connected and ready to receive payouts to your bank.'
                : connected
                  ? 'You started onboarding but it isn’t finished. Continue to enable payouts.'
                  : 'Connect with Stripe to receive your sales payouts directly to your bank account.'}
            </p>
          </div>
        </div>

        <button
          onClick={handleConnect}
          disabled={connecting || loadingStatus}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '12px 24px', background: '#635bff', color: 'white', border: 'none',
            borderRadius: '8px', fontSize: '16px', fontWeight: 600, width: '100%',
            cursor: connecting || loadingStatus ? 'not-allowed' : 'pointer',
            opacity: connecting || loadingStatus ? 0.6 : 1,
          }}
        >
          <CreditCard size={20} />
          {connecting ? 'Redirecting to Stripe…'
            : complete ? 'Manage payout account'
            : connected ? 'Continue Stripe onboarding'
            : 'Connect with Stripe'}
        </button>

        <p style={{ fontSize: '12px', color: 'var(--textSecondary)', marginTop: '12px', textAlign: 'center' }}>
          Secure onboarding handled by Stripe. You'll provide your bank details and identity directly to Stripe.
        </p>
      </div>

      {/* Instant payout (debit card) — only when connected */}
      {complete && (
        <div style={{ background: 'var(--cardBackground)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>⚡ Instant payout</h2>
          <p style={{ color: 'var(--textSecondary)', fontSize: '14px', marginBottom: '16px' }}>
            Cash out your available balance to a debit card in minutes (a small Stripe fee applies).
            Only funds that have already cleared can be cashed out — payments still settle on the normal
            schedule first. Add a debit card in your Stripe dashboard to use this.
          </p>
          {balance && (
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '16px', fontSize: '14px' }}>
              <div><strong style={{ color: 'var(--success)' }}>${(balance.instantAvailable / 100).toFixed(2)}</strong><div style={{ color: 'var(--textSecondary)', fontSize: '12px' }}>Available now</div></div>
              <div><strong>${(balance.pending / 100).toFixed(2)}</strong><div style={{ color: 'var(--textSecondary)', fontSize: '12px' }}>Still settling</div></div>
            </div>
          )}
          <button
            onClick={handleInstantPayout}
            disabled={cashingOut || (balance && balance.instantAvailable <= 0)}
            className="btn btn-primary"
            style={{ width: '100%', opacity: (cashingOut || (balance && balance.instantAvailable <= 0)) ? 0.6 : 1 }}
          >
            {cashingOut ? 'Sending…'
              : balance && balance.instantAvailable > 0
                ? `Cash out $${(balance.instantAvailable / 100).toFixed(2)} instantly`
                : 'Cash out instantly'}
          </button>
        </div>
      )}

      {/* How It Works */}
      <div style={{ background: 'var(--cardBackground)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px' }}>
        <h3 style={{ marginTop: 0 }}>How Payouts Work</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[
            ['1', 'Buyer completes purchase', 'Their payment is held securely until delivery is confirmed.'],
            ['2', 'Ship & confirm delivery', 'Mark the item shipped; the buyer confirms (or it auto-releases after the inspection period).'],
            ['3', 'Get paid via Stripe', 'Your share (item price minus our commission) transfers to your connected Stripe account and pays out to your bank.'],
          ].map(([n, title, desc]) => (
            <div key={n} style={{ display: 'flex', gap: '12px' }}>
              <div style={{ width: '32px', height: '32px', background: 'var(--primary)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, flexShrink: 0 }}>{n}</div>
              <div>
                <strong>{title}</strong>
                <p style={{ color: 'var(--textSecondary)', fontSize: '14px', margin: '4px 0 0 0' }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '24px', padding: '16px', background: 'var(--background)', borderRadius: '8px' }}>
          <p style={{ fontSize: '14px', color: 'var(--textSecondary)', margin: 0 }}>
            <strong>Note:</strong> You must finish Stripe onboarding before you can receive payouts. Shipping and the buyer service fee are not part of your payout.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;
